import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { HiArrowPath } from 'react-icons/hi2';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  getSalesReport, getCollectionsReport, getInventoryReport, getCustomersReport,
} from '../../services/api';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);

const TABS = ['sales', 'collections', 'inventory', 'customers'];

export default function Reports() {
  const [activeTab, setActiveTab] = useState('sales');

  // Sales state
  const [salesData, setSalesData] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesFrom, setSalesFrom] = useState('');
  const [salesTo, setSalesTo] = useState('');

  // Collections state
  const [collectionsData, setCollectionsData] = useState(null);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [colFrom, setColFrom] = useState('');
  const [colTo, setColTo] = useState('');

  // Inventory state
  const [inventoryData, setInventoryData] = useState(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);

  // Customers state
  const [customersData, setCustomersData] = useState(null);
  const [customersLoading, setCustomersLoading] = useState(false);

  const fetchSales = useCallback(() => {
    setSalesLoading(true);
    const params = { from: salesFrom || undefined, to: salesTo || undefined };
    getSalesReport(params)
      .then((res) => setSalesData(res.data))
      .catch(() => toast.error('Failed to load sales report.'))
      .finally(() => setSalesLoading(false));
  }, [salesFrom, salesTo]);

  const fetchCollections = useCallback(() => {
    setCollectionsLoading(true);
    const params = { from: colFrom || undefined, to: colTo || undefined };
    getCollectionsReport(params)
      .then((res) => setCollectionsData(res.data))
      .catch(() => toast.error('Failed to load collections report.'))
      .finally(() => setCollectionsLoading(false));
  }, [colFrom, colTo]);

  const fetchInventory = useCallback(() => {
    setInventoryLoading(true);
    getInventoryReport()
      .then((res) => setInventoryData(res.data))
      .catch(() => toast.error('Failed to load inventory report.'))
      .finally(() => setInventoryLoading(false));
  }, []);

  const fetchCustomers = useCallback(() => {
    setCustomersLoading(true);
    getCustomersReport()
      .then((res) => setCustomersData(res.data))
      .catch(() => toast.error('Failed to load customers report.'))
      .finally(() => setCustomersLoading(false));
  }, []);

  // Load data when tab first becomes active
  useEffect(() => {
    if (activeTab === 'sales' && !salesData) fetchSales();
    if (activeTab === 'collections' && !collectionsData) fetchCollections();
    if (activeTab === 'inventory' && !inventoryData) fetchInventory();
    if (activeTab === 'customers' && !customersData) fetchCustomers();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const salesRows = Array.isArray(salesData) ? salesData : salesData?.data || salesData?.sales || [];
  const salesChartData = salesRows.map((r) => ({ period: r.period || r.date || r.month, revenue: Number(r.revenue || r.total || 0), orders: Number(r.orders || r.count || 0) }));

  const collectionRows = Array.isArray(collectionsData) ? collectionsData : collectionsData?.data || collectionsData?.collections || [];
  const inventoryRows = Array.isArray(inventoryData) ? inventoryData : inventoryData?.data || inventoryData?.products || [];
  const customerRows = Array.isArray(customersData) ? customersData : customersData?.data || customersData?.customers || [];

  return (
    <div className="content-area">
      <div className="page-header">
        <h1 className="page-title">Reports</h1>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.5rem', borderBottom: '2px solid var(--border)' }}>
        {TABS.map((tab) => (
          <button
            key={tab}
            className="btn btn-secondary"
            style={{
              borderRadius: '0.375rem 0.375rem 0 0',
              borderBottom: activeTab === tab ? '2px solid var(--blue)' : '2px solid transparent',
              color: activeTab === tab ? 'var(--blue)' : undefined,
              fontWeight: activeTab === tab ? 600 : 400,
              marginBottom: '-2px',
            }}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Sales Tab */}
      {activeTab === 'sales' && (
        <>
          <div className="filter-bar" style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <label className="form-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>From:</label>
              <input type="date" className="form-control" style={{ width: '150px' }} value={salesFrom} onChange={(e) => setSalesFrom(e.target.value)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <label className="form-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>To:</label>
              <input type="date" className="form-control" style={{ width: '150px' }} value={salesTo} onChange={(e) => setSalesTo(e.target.value)} />
            </div>
            <button className="btn btn-primary btn-sm" onClick={fetchSales} disabled={salesLoading}>
              <HiArrowPath /> {salesLoading ? 'Loading...' : 'Apply'}
            </button>
          </div>

          {salesLoading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : (
            <>
              {salesChartData.length > 0 && (
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <div className="card-header"><span className="card-title">Revenue by Period</span></div>
                  <div className="card-body">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={salesChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `Rs.${(v / 1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v, name) => [name === 'revenue' ? `Rs.${fmt(v)}` : v, name === 'revenue' ? 'Revenue' : 'Orders']} />
                        <Legend />
                        <Bar dataKey="revenue" fill="#0ea5e9" name="Revenue" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="orders" fill="#8b5cf6" name="Orders" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              <div className="card">
                <div className="card-header"><span className="card-title">Sales Data</span></div>
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Period</th>
                        <th>Orders</th>
                        <th>Revenue Rs.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesRows.length === 0 ? (
                        <tr><td colSpan={3} style={{ textAlign: 'center', color: '#94a3b8' }}>No data for selected range.</td></tr>
                      ) : (
                        salesRows.map((r, i) => (
                          <tr key={i}>
                            <td>{r.period || r.date || r.month || '-'}</td>
                            <td>{fmt(r.orders || r.count)}</td>
                            <td>Rs.{fmt(r.revenue || r.total)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Collections Tab */}
      {activeTab === 'collections' && (
        <>
          <div className="filter-bar" style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <label className="form-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>From:</label>
              <input type="date" className="form-control" style={{ width: '150px' }} value={colFrom} onChange={(e) => setColFrom(e.target.value)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <label className="form-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>To:</label>
              <input type="date" className="form-control" style={{ width: '150px' }} value={colTo} onChange={(e) => setColTo(e.target.value)} />
            </div>
            <button className="btn btn-primary btn-sm" onClick={fetchCollections} disabled={collectionsLoading}>
              <HiArrowPath /> {collectionsLoading ? 'Loading...' : 'Apply'}
            </button>
          </div>

          {collectionsLoading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : (
            <div className="card">
              <div className="card-header"><span className="card-title">Collections</span></div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Method</th>
                      <th>Amount Rs.</th>
                      <th>Customer</th>
                      <th>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {collectionRows.length === 0 ? (
                      <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8' }}>No collections for selected range.</td></tr>
                    ) : (
                      collectionRows.map((r, i) => (
                        <tr key={i}>
                          <td>{r.date ? new Date(r.date).toLocaleDateString('en-IN') : r.payment_date ? new Date(r.payment_date).toLocaleDateString('en-IN') : '-'}</td>
                          <td>{r.method || r.payment_method || '-'}</td>
                          <td>Rs.{fmt(r.amount)}</td>
                          <td>{r.customer_name || r.customer?.name || '-'}</td>
                          <td>{r.transaction_ref || r.reference || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Inventory Tab */}
      {activeTab === 'inventory' && (
        <>
          <div className="filter-bar" style={{ marginBottom: '1rem' }}>
            <button className="btn btn-primary btn-sm" onClick={fetchInventory} disabled={inventoryLoading}>
              <HiArrowPath /> {inventoryLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
          {inventoryLoading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : (
            <div className="card">
              <div className="card-header"><span className="card-title">Inventory Report</span></div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th>Current Stock</th>
                      <th>Total In</th>
                      <th>Total Out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryRows.length === 0 ? (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8' }}>No inventory data.</td></tr>
                    ) : (
                      inventoryRows.map((r, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight: 500 }}>{r.name || r.product_name}</td>
                          <td style={{ fontWeight: 600, color: (Number(r.current_stock) || 0) < 20 ? '#ef4444' : 'inherit' }}>
                            {fmt(r.current_stock ?? r.stock_quantity)}
                          </td>
                          <td>{fmt(r.total_in)}</td>
                          <td>{fmt(r.total_out)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Customers Tab */}
      {activeTab === 'customers' && (
        <>
          <div className="filter-bar" style={{ marginBottom: '1rem' }}>
            <button className="btn btn-primary btn-sm" onClick={fetchCustomers} disabled={customersLoading}>
              <HiArrowPath /> {customersLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
          {customersLoading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : (
            <div className="card">
              <div className="card-header"><span className="card-title">Top Customers by Spending</span></div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Customer Name</th>
                      <th>Phone</th>
                      <th>Total Orders</th>
                      <th>Total Spending Rs.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerRows.length === 0 ? (
                      <tr><td colSpan={5} style={{ textAlign: 'center', color: '#94a3b8' }}>No customer data.</td></tr>
                    ) : (
                      customerRows.map((r, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td style={{ fontWeight: 500 }}>{r.name || r.customer_name}</td>
                          <td>{r.phone || '-'}</td>
                          <td>{fmt(r.total_orders || r.orders)}</td>
                          <td>Rs.{fmt(r.total_spending || r.total_revenue || r.revenue)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
